import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCookieConsent } from '../contexts/CookieConsentContext';

// Banner de cookies/LGPD, site-wide (aparece em toda página pública, inclusive as chromeless
// /l/:slug e /a/:slug — são justamente as páginas de captação, cookies não são escopo de uma
// única página). GoogleAnalytics/MetaPixel só disparam depois que `consent.analytics`/`marketing`
// forem true.
const CookieConsentBanner: React.FC = () => {
  const { t } = useTranslation();
  const { hasDecided, acceptAll, rejectNonEssential, savePreferences } = useCookieConsent();
  const [showPreferences, setShowPreferences] = useState(false);
  const [draftAnalytics, setDraftAnalytics] = useState(true);
  const [draftMarketing, setDraftMarketing] = useState(true);

  if (hasDecided) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('cookieConsent.customizeTitle')}
      className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4"
    >
      <div className="card mx-auto max-w-3xl border border-gray-200 shadow-2xl">
        {!showPreferences ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-700">
              {t('cookieConsent.message')}{' '}
              <Link to="/privacidade" className="underline text-primary-600 hover:text-primary-700">
                {t('cookieConsent.privacyLink')}
              </Link>
              .
            </p>
            <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0">
              <button type="button" onClick={() => setShowPreferences(true)} className="btn-outline px-3 py-2 text-sm">
                {t('cookieConsent.customize')}
              </button>
              <button type="button" onClick={rejectNonEssential} className="btn-outline px-3 py-2 text-sm">
                {t('cookieConsent.rejectNonEssential')}
              </button>
              <button type="button" onClick={acceptAll} className="btn-primary px-3 py-2 text-sm">
                {t('cookieConsent.acceptAll')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <h2 className="text-base font-semibold text-gray-900">{t('cookieConsent.customizeTitle')}</h2>

            <div className="rounded-lg border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{t('cookieConsent.necessaryTitle')}</span>
                <input type="checkbox" checked disabled aria-label={t('cookieConsent.necessaryTitle')} />
              </div>
              <p className="mt-1 text-xs text-gray-500">{t('cookieConsent.necessaryDesc')}</p>
            </div>

            <div className="rounded-lg border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{t('cookieConsent.analyticsTitle')}</span>
                <input
                  type="checkbox"
                  checked={draftAnalytics}
                  onChange={(e) => setDraftAnalytics(e.target.checked)}
                  aria-label={t('cookieConsent.analyticsTitle')}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">{t('cookieConsent.analyticsDesc')}</p>
            </div>

            <div className="rounded-lg border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{t('cookieConsent.marketingTitle')}</span>
                <input
                  type="checkbox"
                  checked={draftMarketing}
                  onChange={(e) => setDraftMarketing(e.target.checked)}
                  aria-label={t('cookieConsent.marketingTitle')}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">{t('cookieConsent.marketingDesc')}</p>
            </div>

            <div className="form-actions">
              <button type="button" onClick={() => setShowPreferences(false)} className="btn-outline px-3 py-2 text-sm">
                {t('cancelar', { defaultValue: 'Cancelar' })}
              </button>
              <button
                type="button"
                onClick={() => savePreferences({ analytics: draftAnalytics, marketing: draftMarketing })}
                className="btn-primary px-3 py-2 text-sm"
              >
                {t('cookieConsent.save')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;
